import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Search } from 'lucide-react'
import { users as userApi } from '@/api/endpoints'
import type { Team, UserList } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useDragPick } from '@/hooks/useDragPick'
import { peopleChanged, usePage } from '@/hooks/usePage'
import { DASH } from '@/ui/format'
import { personRow } from '@/views/model'
import type { Candidate } from './teamPick'

// `팀 관리`·`직위`·`권한` 창과 팀 화면의 멤버 창의 왼쪽 칸 — 대상인 사람들을 고른다(F-66 · F-72 · F-81 · F-83).
// 고른 사람만 보이는 목록과 모두에게서 고르는 자리 둘이고, 아무도 고르지 않은 채 열면 고르는 자리로 선다 —
// 문장이 아니라 화면의 모양이 그것을 말한다. 고르는 자리에서는 줄을 끌어 여러 사람을 한 번에 고르고 푼다.
// 목록의 높이는 고정이다. 검색으로 줄어도 그 아래 버튼이 제자리에 남아야 한다.
// **고르는 자리는 서버의 찾기다**(`GET /users` 의 `q`, 탈퇴하지 않은 사람) — 사람이 수천인 배포에서 모두를 받아 거르지 않는다.
// 한 번에 `LIMIT` 명까지 받고, 맞는 사람이 더 있으면 그 수를 `받은/맞은명` 으로 보인다 — 찾는 말을 좁히라는 뜻이다.
// 무엇으로 찾는지는 스펙의 `listUsers` 가 원본이다. 찾은 사람의 소속은 `World` 의 팀 목록에서 잇는다(`personRow`).
// **고른 사람은 부르는 창이 사람째로 쥔다**(`hooks/usePicked.ts`) — 고른 목록은 그것을 그리고, 찾은 목록에서 다시 찾지 않는다.
// 찾기가 새로 읽히면 그 줄을 `onFound` 로 넘겨 쥔 사람을 새 줄로 바꾸게 한다 — 줄의 `aside` 가 고른 순간의 값으로 남지 않게.
// `aside` 는 그 창이 결정하는 값의 지금 상태다(직위 창은 지금 직위, 권한 창은 지금 권한) —
// 이미 그 값인 사람을 가려내지 못하면 같은 적용을 되풀이한다.
// `locked` 는 고르기를 바꿀 수 없는 사람이다 — 서버가 거절할 것을 창이 먼저 안다(팀의 마지막 관리자).
// 체크박스가 잠기고 줄을 눌러도 끌어도 그 사람은 그대로다. `omit` 은 후보가 아닌 사람이다(권한 창의 자기 자신) —
// 받은 쪽에 있으면 `받은/맞은명` 의 두 수에서도 덜어 낸다. 세면 수가 보이는 줄보다 많다.
// `control` 은 고른 사람마다 그 창이 바꾸는 값의 조작이다(팀 멤버 창의 `관리자`). 고른 사람만 보이는 목록에서
// `aside` 자리에 서고 고르는 자리에는 서지 않는다 — 그 줄은 누르고 끌어 고르는 자리라 누르기가 겹친다.

/** 한 번에 찾아 보이는 사람의 수. */
const LIMIT = 100
// 사람의 목록이라 사용자 목록과 같은 간격이다 — 변경 알림(최고 관리자)과 쓰기 뒤의 다시 읽기가 그 사이를 메운다.
const POLL_MS = 8000

export const LIST = 'h-[22rem] overflow-y-auto rounded-md border max-sm:h-52'
export const ROW = 'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] hover:bg-surface-2'

export function PeoplePicker({
  id,
  teams,
  chosen,
  onMember,
  onFound,
  omit,
  aside,
  locked,
  control,
}: {
  id: string
  /** 찾은 사람의 소속을 잇는 팀 목록(`World` 의 것). */
  teams: Team[]
  /** 고른 사람 — 부르는 창이 쥔 것(`usePicked`). */
  chosen: Candidate[]
  onMember: (p: Candidate, on: boolean) => void
  /** 찾기가 새로 읽혔다 — 부르는 창이 쥔 사람을 새 줄로 바꾼다(`usePicked` 의 `renew`). */
  onFound: (rows: Candidate[]) => void
  omit?: (p: Candidate) => boolean
  aside?: (p: Candidate) => string | undefined
  locked?: (p: Candidate) => boolean
  control?: (p: Candidate) => ReactNode
}) {
  const [mode, setMode] = useState<'list' | 'pick'>(chosen.length === 0 ? 'pick' : 'list')
  const [find, setFind] = useState('')
  // 고른 목록에 있는 동안에도 읽어 둔다 — 고르는 자리로 바꾸는 순간 줄이 비어 있지 않게.
  const found = usePage<UserList>({
    read: ({ terms, limit, offset }) => userApi.page({ q: terms, withdrawn: false, limit, offset }),
    terms: [find],
    filters: null,
    match: peopleChanged,
    pollMs: POLL_MS,
    size: LIMIT,
  })
  const rows = (found.data?.items ?? []).map((u) => personRow(u, teams)).filter((p) => omit?.(p) !== true)
  useEffect(() => {
    if (found.data !== null) onFound(found.data.items.map((u) => personRow(u, teams)))
  }, [found.data, teams, onFound])
  const got = rows.length
  // 받은 쪽 밖의 뺀 사람은 알 길이 없어 맞은 수에 남는다.
  const total = found.data?.total === undefined ? undefined : found.data.total - (found.data.items.length - got)
  const picked = new Set(chosen.map((p) => p.user.id))
  const fixed = (p: Candidate) => locked?.(p) === true
  const set = (uid: string, on: boolean) => {
    const p = rows.find((x) => x.user.id === uid) ?? chosen.find((x) => x.user.id === uid)
    if (p !== undefined && !fixed(p)) onMember(p, on)
  }
  const drag = useDragPick((uid) => picked.has(uid), set)

  const shown = mode === 'pick' ? rows : chosen
  const empty = mode === 'pick' && found.data === null ? (found.loading ? '불러오는 중' : null) : '사용자 없음'

  return (
    <div className="grid content-start gap-2">
      <div className="flex items-center gap-2">
        <Label id={id}>사용자</Label>
        {mode === 'pick' && total !== undefined && total > got ? (
          <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">{`${got}/${total}명`}</span>
        ) : null}
      </div>
      {mode === 'pick' ? (
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={find}
            onChange={(e) => setFind(e.target.value)}
            aria-label="사용자 검색"
            maxLength={100}
            className="h-8 rounded-full bg-background pl-9 text-base sm:text-[13px]"
          />
        </div>
      ) : null}
      {mode === 'pick' ? <Err error={found.error} /> : null}
      {/* 끄는 동안 글자가 칠해지지 않게 고르는 자리는 선택을 막는다. */}
      <ul aria-labelledby={id} className={mode === 'pick' ? `${LIST} select-none` : LIST}>
        {shown.length === 0 ? (
          empty === null ? null : <li className="px-3 py-2 text-[12px] text-muted-foreground">{empty}</li>
        ) : (
          shown.map((p) =>
            mode === 'pick' ? (
              <li key={p.user.id} className={ROW} {...drag(p.user.id)}>
                <Checkbox
                  aria-label={`${p.user.username} 고르기`}
                  checked={picked.has(p.user.id)}
                  disabled={fixed(p)}
                  onCheckedChange={(on) => set(p.user.id, on === true)}
                />
                <Who person={p} aside={aside} />
              </li>
            ) : (
              <li key={p.user.id} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
                <Who person={p} aside={control ? undefined : aside} />
                {control?.(p)}
              </li>
            ),
          )
        )}
      </ul>
      {/* 스크롤 밖이다 — 사람이 몇이든 이 버튼은 목록 아래 제자리에 있다. */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setMode(mode === 'pick' ? 'list' : 'pick')
          setFind('')
        }}
      >
        {mode === 'pick' ? '고르기 마침' : '유저 목록 수정'}
      </Button>
    </div>
  )
}

// 한 사람 줄: 아이디 · 이름 · 그 창이 정하는 값. 그 값이 줄어들지 않는 칸인 것은 창이 그것으로 결정하기
// 때문이고, 줄어드는 것은 이름이다.
function Who({ person, aside }: { person: Candidate; aside: ((p: Candidate) => string | undefined) | undefined }) {
  return (
    <>
      <span className="truncate font-medium">{person.user.username}</span>
      <span className="ml-auto min-w-0 truncate text-muted-foreground">{person.user.name ?? DASH}</span>
      {aside ? <span className="shrink-0 text-[11px] text-muted-foreground">{aside(person) ?? DASH}</span> : null}
    </>
  )
}
